import React, { ReactNode, useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import Button from 'components/Button'
import StyledFileUploader from './FileUploader.style'
import { uploadToCloudinary, normalizeCloudinaryResult, type NormalizedFile } from './fileUploadUtils'

export interface Props {
	className?: string
	fileType?: 'image' | 'video' | 'document'
	buttonLabel?: string
	children?: ReactNode
	onChange: (file: NormalizedFile) => void
}

const FileUploader = ({ className = '', fileType, buttonLabel, children, onChange }: Props) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const [isUploading, setIsUploading] = useState(false)

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		inputRef.current?.click()
	}

	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (!file) return
			setIsUploading(true)
			try {
				const result = await uploadToCloudinary(file)
				onChange(normalizeCloudinaryResult(result, 'browse'))
			} catch (err) {
				console.error('[FileUploader] Upload failed', err)
			} finally {
				setIsUploading(false)
				if (inputRef.current) inputRef.current.value = ''
			}
		},
		[onChange],
	)

	const acceptType = fileType === 'image' ? 'image/*' : fileType === 'video' ? 'video/*' : undefined

	return (
		<StyledFileUploader data-testid="FileUploader" className={className}>
			<Button className="uploadcare--widget__button" onClick={handleClick} variant="ghost" disabled={isUploading}>
				{fileType && fileType === 'image' && (
					<>
						<Upload className="w-4 h-4" /> {buttonLabel ? buttonLabel : 'Upload Image'}
					</>
				)}
				{fileType && fileType === 'video' && (
					<>
						<Upload className="w-4 h-4" /> {buttonLabel ? buttonLabel : 'Upload Video'}
					</>
				)}
				{fileType && fileType === 'document' && (
					<div className="flex items-center justify-center gap-2">
						<Upload className="w-4 h-4" />
						{buttonLabel ? buttonLabel : 'Upload Document'}
					</div>
				)}
				{!fileType && children}
			</Button>
			<input
				ref={inputRef}
				type="file"
				accept={acceptType}
				onChange={handleFileChange}
				className="hidden"
				aria-label="File upload"
			/>
		</StyledFileUploader>
	)
}

export default FileUploader
